import { ReactElement } from 'react';

import {
   AppointmentDetailsQuery,
   AppointmentEncounterMode,
   AppointmentUpdateEncounterModeCommand,
} from '@dreryk/egabinet-appointment-frontend-api';
import { AppointmentId } from '@dreryk/egabinet-appointment-published-language-api';

import { MapMarkerIcon, VideoConsultationIcon } from '@dreryk/react-components';
import { refetchCqrsQuery, useCqrsCommand, UseCqrsCommandResult } from '@dreryk/command-query-react-common';

import { patientTranslationKeys } from '../../../translations';

import { VideoConsultationDropdownItem } from './items/VideoConsultationDropdownItem';
import { OnSiteDropdownItem } from './items/OnSiteDropdownItem';

const t = patientTranslationKeys.encounterMode;

type UseAppointmentUpdateEncounterModeCommandOptions = Readonly<{ appointmentId: number }>;

export const useAppointmentUpdateEncounterModeCommand = (
   options: UseAppointmentUpdateEncounterModeCommandOptions
): UseCqrsCommandResult<void, AppointmentEncounterMode> => {
   return useCqrsCommand<void, AppointmentEncounterMode>(
      (encounterMode) => {
         const command = new AppointmentUpdateEncounterModeCommand();
         command.id = AppointmentId.of(options.appointmentId);
         command.encounterMode = encounterMode;
         return command;
      },
      {
         onSuccess: async () => refetchCqrsQuery(AppointmentDetailsQuery),
      }
   );
};

type DropdownItemsConfiguration = Record<AppointmentEncounterMode, DropdownItem>;
type DropdownItem = { iconTestId: string; icon: ReactElement; label: string; item: ReactElement };

type OnUpdateAppointmentEncounterMode = (encounterMode: AppointmentEncounterMode) => Promise<void>;

type UseAppointmentEncounterModeDropdownItemsConfigurationOptions = Readonly<{
   onUpdateAppointmentEncounterMode: OnUpdateAppointmentEncounterMode;
}>;

type UseAppointmentEncounterModeDropdownItemsConfigurationReturn = DropdownItemsConfiguration;

export const useAppointmentEncounterModeDropdownItemsConfiguration = (
   options: UseAppointmentEncounterModeDropdownItemsConfigurationOptions
): UseAppointmentEncounterModeDropdownItemsConfigurationReturn => ({
   ON_SITE: {
      iconTestId: 'mapMarker',
      icon: <MapMarkerIcon />,
      label: t.ON_SITE,
      item: <OnSiteDropdownItem key={'mapMarker'} onClick={options.onUpdateAppointmentEncounterMode} />,
   },
   VIDEO_CONSULTATION: {
      iconTestId: 'videoConsultation',
      icon: <VideoConsultationIcon />,
      label: t.VIDEO_CONSULTATION,
      item: (
         <VideoConsultationDropdownItem key={'videoConsultation'} onClick={options.onUpdateAppointmentEncounterMode} />
      ),
   },
});
